import { RouteComponentProps } from '@client/router/compat';
import { FunctionComponent, useEffect, useMemo } from 'react';
import _ from 'lodash';
import toast from 'react-hot-toast';
import { Tabs, Tab, Box } from '@mui/material';
import {
  useGetCommunityQuery,
  useGetReportedContentCountQuery,
} from '../../../generated/graphql';
import auth from '../../components/auth';
import { navigate, PATHS } from '../../routes';
import { CommunityFeed } from './components/Feed';
import ReportedContent from './components/ReportedContent';
import { BlockedUsers } from './components/BlockedUsers';
import { CommunityLayout } from './components/CommunityLayout';
import { useDmFeatureEnabled } from './hooks/useDmFeatureEnabled';
import { useTabHash } from '../hooks/useTabHash';

type Props = { component: FunctionComponent } & RouteComponentProps<
  { id: string; influencerId: string },
  unknown
>;

const getTabs = (reportedContentCount: number) => [
  {
    value: 'feed',
    label: 'Feed',
  },
  {
    value: 'reports',
    label: `Reported Content (${reportedContentCount})`,
  },
  {
    value: 'blocked',
    label: 'Blocked Users',
  },
];

const tabs = ['feed', 'reports', 'blocked'] as const;

export const CommunityPage = (props: Props) => {
  const influencerId = parseInt(props.params.influencerId);
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const communityId = Number(props.params.id);
  const { tabHash: tab, onTabChange } = useTabHash(tabs, 'feed');
  const { isDmFeatureEnabled } = useDmFeatureEnabled(influencerId);

  const { data: communityData } = useGetCommunityQuery({
    variables: { id: +communityId },
    fetchPolicy: 'network-only',
  });

  const { data: contentCount } = useGetReportedContentCountQuery({
    variables: { communityId: +communityId },
  });

  useEffect(() => {
    if (communityData?.community === null) {
      if (isAdminModerator) {
        navigate(PATHS.HOME);
        return;
      }
      navigate(PATHS.CREATOR_COMMUNITIES(influencerId));
    }

    if (!isAdminModerator && communityData?.community?.owner) {
      const user = auth.getUser();
      if (communityData?.community?.owner.id !== user?.influencerId) {
        toast.error('Wrong community id. Try menu.');
        navigate(PATHS.HOME);
      }
    }
  }, [communityData?.community]);

  const ownerName = communityData?.community?.owner.username || 'Creator';
  const ownerProfilePicture =
    communityData?.community?.owner.profilePicture || '';
  const isUserCommunityOwner =
    communityData?.community?.owner.badges.includes('own') || false;

  const communityTabs = useMemo(
    () => getTabs(contentCount?.reportedContentCount || 0),
    [contentCount?.reportedContentCount],
  );

  return (
    <CommunityLayout
      communityId={+communityId}
      ownerName={ownerName}
      influencerId={influencerId}
    >
      <Box className="h-full max-w-[680px] my-0 mx-auto flex flex-col min-h-[200px]">
        <Tabs
          value={tab}
          onChange={onTabChange}
          className="mb-[30px] border-b-[#ECEDF2] border-b-[3px]"
        >
          {communityTabs.map(({ value, label }) => (
            <Tab key={value} value={value} label={label} />
          ))}
        </Tabs>
        {tab === 'feed' && (
          <CommunityFeed
            influencerId={influencerId}
            communityId={communityId}
            user={{
              name: ownerName,
              picture: ownerProfilePicture,
              isOwner: isUserCommunityOwner,
            }}
            isDmFeatureEnabled={isDmFeatureEnabled}
          />
        )}
        {tab === 'reports' && (
          <ReportedContent
            communityId={communityId}
            influencerId={influencerId}
          />
        )}
        {tab === 'blocked' && <BlockedUsers communityId={communityId} />}
      </Box>
    </CommunityLayout>
  );
};
